import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import * as XLSX from 'xlsx';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useSession } from '../lib/useSession';

// GA4 event tracking helper. Safe no-op if gtag isn't loaded.
const track = (event, params = {}) => {
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', event, params);
    }
  } catch (_) {}
};

// Bucket a text length into a coarse range for analytics.
const lengthBucket = (len) => {
  if (len <= 100) return '0-100';
  if (len <= 500) return '100-500';
  if (len <= 2000) return '500-2000';
  return '2000+';
};

const ExtractStudyApp = () => {
  const [inputText, setInputText] = useState('');
  const [flashcards, setFlashcards] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [activeTab, setActiveTab] = useState(() => {
    // Restore tab after iOS evicts the page when capture="environment" opens the camera app
    try { return sessionStorage.getItem('_es_cam_tab') || 'text'; } catch { return 'text'; }
  });
  const [uploadedImage, setUploadedImage] = useState(null);
  const [uploadedImageSource, setUploadedImageSource] = useState('');
  const [rawImage, setRawImage] = useState(null);
  const [isCropping, setIsCropping] = useState(false);
  const [crop, setCrop] = useState({ unit: '%', width: 90, x: 5, y: 5 });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [error, setError] = useState('');
  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [processingComplete, setProcessingComplete] = useState(false);
  const [loadingStage, setLoadingStage] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('spanish');
  const [searchLanguage, setSearchLanguage] = useState('');
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [useCompactMobileHeader, setUseCompactMobileHeader] = useState(false);

  // Auth (Supabase email/password — see /login, /signup pages)
  const navigate = useNavigate();
  const { session } = useSession();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const profileMenuRef = useRef(null);

  const supportedLanguages = [
    { code: 'spanish', name: 'Spanish' },
    { code: 'french', name: 'French' },
    { code: 'german', name: 'German' },
    { code: 'chinese-mandarin', name: 'Chinese (Mandarin)' },
    { code: 'japanese', name: 'Japanese' },
    { code: 'korean', name: 'Korean' },
    { code: 'italian', name: 'Italian' },
    { code: 'portuguese', name: 'Portuguese' },
    { code: 'russian', name: 'Russian' },
    { code: 'arabic', name: 'Arabic (Modern Standard)' },
    { code: 'dutch', name: 'Dutch' },
    { code: 'turkish', name: 'Turkish' },
    { code: 'vietnamese', name: 'Vietnamese' },
    { code: 'indonesian', name: 'Indonesian' },
    { code: 'hindi', name: 'Hindi' },
    { code: 'bengali', name: 'Bengali' },
    { code: 'japanese-kanji', name: 'Japanese (Kanji)' },
    { code: 'korean-honorific', name: 'Korean (Honorific)' },
    { code: 'thai', name: 'Thai' },
    { code: 'malay', name: 'Malay' },
    { code: 'polish', name: 'Polish' },
    { code: 'swedish', name: 'Swedish' },
    { code: 'danish', name: 'Danish' },
    { code: 'norwegian', name: 'Norwegian' },
    { code: 'finnish', name: 'Finnish' },
    { code: 'greek', name: 'Greek' },
    { code: 'czech', name: 'Czech' },
    { code: 'romanian', name: 'Romanian' },
    { code: 'ukrainian', name: 'Ukrainian' },
    { code: 'hebrew', name: 'Hebrew' },
    { code: 'persian', name: 'Persian (Farsi)' },
    { code: 'urdu', name: 'Urdu' },
    { code: 'swahili', name: 'Swahili' },
    { code: 'hungarian', name: 'Hungarian' },
    { code: 'bulgarian', name: 'Bulgarian' },
    { code: 'serbian', name: 'Serbian' },
    { code: 'croatian', name: 'Croatian' },
    { code: 'slovak', name: 'Slovak' },
    { code: 'lithuanian', name: 'Lithuanian' },
    { code: 'latvian', name: 'Latvian' },
    { code: 'estonian', name: 'Estonian' },
    { code: 'slovenian', name: 'Slovenian' },
    { code: 'macedonian', name: 'Macedonian' },
    { code: 'albanian', name: 'Albanian' },
    { code: 'georgian', name: 'Georgian' },
    { code: 'armenian', name: 'Armenian' },
    { code: 'azerbaijani', name: 'Azerbaijani' },
    { code: 'kazakh', name: 'Kazakh' },
    { code: 'uzbek', name: 'Uzbek' },
    { code: 'tamil', name: 'Tamil' },
    { code: 'telugu', name: 'Telugu' },
    { code: 'marathi', name: 'Marathi' },
    { code: 'gujarati', name: 'Gujarati' },
    { code: 'punjabi', name: 'Punjabi' },
    { code: 'malayalam', name: 'Malayalam' },
    { code: 'kannada', name: 'Kannada' },
    { code: 'odia', name: 'Odia' },
    { code: 'sinhala', name: 'Sinhala' },
  ];
  
  // Filtered languages based on search
  const filteredLanguages = searchLanguage.trim() === '' 
    ? supportedLanguages 
    : supportedLanguages.filter(lang => 
        lang.name.toLowerCase().includes(searchLanguage.toLowerCase()) ||
        lang.code.toLowerCase().includes(searchLanguage.toLowerCase())
      );
  
  // Reference for closing dropdown when clicking outside
  const languageDropdownRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const cropImgRef = useRef(null);
  
  // Restore state saved before iOS camera eviction
  useEffect(() => {
    try {
      const pendingTab = sessionStorage.getItem('_es_cam_tab');
      if (pendingTab) {
        sessionStorage.removeItem('_es_cam_tab');
        const saved = sessionStorage.getItem('_es_cam_state');
        if (saved) {
          sessionStorage.removeItem('_es_cam_state');
          const s = JSON.parse(saved);
          if (s.flashcards?.length) setFlashcards(s.flashcards);
          if (s.inputText) setInputText(s.inputText);
          if (s.selectedLanguage) setSelectedLanguage(s.selectedLanguage);
        }
      }
    } catch {}
  }, []);

  // Close profile dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const signOut = async () => {
    setShowProfileMenu(false);
    try { await supabase.auth.signOut(); } catch (_) {}
  };

  // When logged in, every generated card is auto-saved to the user's
  // Vocabulary Book. Server-side unique constraint drops repeats.
  const saveGeneratedToVocab = async (cards) => {
    if (!session || !Array.isArray(cards) || cards.length === 0) return;
    try {
      const rows = cards
        .filter((c) => c && c.word && c.translation)
        .map((c) => ({
          user_id: session.user.id,
          language: selectedLanguage,
          word: String(c.word).trim(),
          lemma: String(c.lemma || c.word).trim(),
          pos: c.type ? String(c.type).trim() : null,
          translation: String(c.translation).trim(),
          example: c.example ? String(c.example).trim() : null,
          source: 'generated',
        }));
      if (rows.length === 0) return;
      await supabase
        .from('vocab_cards')
        .upsert(rows, {
          onConflict: 'user_id,lang_norm,lemma_norm,pos_norm',
          ignoreDuplicates: true,
        });
    } catch (_) {
      // Non-blocking: generation UX must never fail because of saving.
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target)) {
        setIsLanguageDropdownOpen(false);
      }
    }
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Use compact header on low-height mobile screens
  useEffect(() => {
    const updateHeaderMode = () => {
      if (typeof window === 'undefined') return;
      const isMobileWidth = window.innerWidth < 768;
      const isLowHeight = window.innerHeight < 900;
      setUseCompactMobileHeader(isMobileWidth && isLowHeight);
    };

    updateHeaderMode();
    window.addEventListener('resize', updateHeaderMode);

    return () => {
      window.removeEventListener('resize', updateHeaderMode);
    };
  }, []);

  
  // Filters
  const [filters, setFilters] = useState({
    noun: true,
    verb: true,
    adjective: true,
    other: true
  });
  
  // New flashcard form
  const [newCard, setNewCard] = useState({
    word: '',
    translation: '',
    type: 'noun'
  });
  
  // Study mode states
  const [studyMode, setStudyMode] = useState(''); // '', 'remember', 'test'
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [showTranslation, setShowTranslation] = useState(false);
  const [isFlipping, setIsFlipping] = useState(false);
  const [startWithSourceLang, setStartWithSourceLang] = useState(true);

  const API_URL = '';
  const progressIntervalRef = useRef(null);

  // Get filtered cards based on type selection
  const filteredCards = flashcards.filter(card => {
    if (card.type === 'noun' && filters.noun) return true;
    if (card.type === 'verb' && filters.verb) return true;
    if (card.type === 'adjective' && filters.adjective) return true;
    if (!['noun', 'verb', 'adjective'].includes(card.type) && filters.other) return true;
    return false;
  });

  const processText = async () => {
    if (!inputText.trim()) return;

    if (new Blob([inputText]).size > 5 * 1024 * 1024) {
      setError('Text size exceeds 5MB limit');
      return;
    }

    track('generate_clicked', {
      language: selectedLanguage,
      input_type: 'text',
      input_length: lengthBucket(inputText.length),
    });

    setIsLoading(true);
    setError('');
    setLoadingProgress(0);
    setProcessingComplete(false);
    setLoadingStage('Parsing vocabulary\u2026');
    if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);

    // Phase 1: 0 → 40% while waiting for first token
    progressIntervalRef.current = setInterval(() => {
      setLoadingProgress(prev => prev >= 40 ? 40 : prev + Math.random() * 3);
    }, 400);

    try {
      const response = await fetch(`${API_URL}/api/process-text-stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: inputText, language: selectedLanguage }),
      });

      if (!response.ok) {
        clearInterval(progressIntervalRef.current);
        const errData = await response.json().catch(() => ({}));
        if (response.status === 429) {
          setError(errData.message || 'Rate limit exceeded. Please try again later.');
        } else {
          setError(`Server error ${response.status}. Please try again.`);
        }
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let sseBuffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        sseBuffer += decoder.decode(value, { stream: true });
        const lines = sseBuffer.split('\n');
        sseBuffer = lines.pop();

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const raw = line.slice(6).trim();
          if (!raw) continue;
          try {
            const event = JSON.parse(raw);
            if (event.type === 'ttft') {
              clearInterval(progressIntervalRef.current);
              setLoadingStage('Building flashcards\u2026');
              setLoadingProgress(42);
              // Phase 2: 42 → 90%
              progressIntervalRef.current = setInterval(() => {
                setLoadingProgress(prev => prev >= 90 ? 90 : prev + Math.random() * 2);
              }, 400);
            } else if (event.type === 'complete') {
              clearInterval(progressIntervalRef.current);
              setFlashcards(event.flashcards);
              track('generation_succeeded', {
                language: selectedLanguage,
                card_count: (event.flashcards || []).length,
              });
              saveGeneratedToVocab(event.flashcards);
              setLoadingProgress(100);
              setProcessingComplete(true);
              setLoadingStage('');
            } else if (event.type === 'error') {
              clearInterval(progressIntervalRef.current);
              setError(event.message || 'Failed to process text. Please try again.');
            }
          } catch (_) {}
        }
      }
    } catch (error) {
      console.error('Error processing text:', error);
      clearInterval(progressIntervalRef.current);
      track('generation_failed', { error: String((error && error.message) || error) });
      setError('Failed to process text. Please try again.');
    } finally {
      clearInterval(progressIntervalRef.current);
      setTimeout(() => {
        setIsLoading(false);
        setLoadingStage('');
      }, 800);
    }
  };

  const compressToUnder5MB = (dataUrl) => {
    return new Promise((resolve) => {
      const MAX = 5 * 1024 * 1024;
      const img = new Image();
      img.onload = () => {
        let w = img.width;
        let h = img.height;
        // Scale down large images (1500px is plenty for OCR, keeps memory low on mobile)
        const maxDim = 1500;
        if (w > maxDim || h > maxDim) {
          const scale = maxDim / Math.max(w, h);
          w = Math.round(w * scale);
          h = Math.round(h * scale);
        }
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        // Iteratively reduce quality until under 5MB
        const tryQ = (q) => {
          const out = canvas.toDataURL('image/jpeg', q);
          const bytes = Math.round((out.length - out.indexOf(',') - 1) * 0.75);
          if (bytes <= MAX || q <= 0.45) { resolve(out); }
          else { tryQ(+(q - 0.08).toFixed(2)); }
        };
        tryQ(0.92);
      };
      img.src = dataUrl;
    });
  };

  const processFileInput = (file, source) => {
    if (!file) return;
    setError('');
    const reader = new FileReader();
    reader.onload = async () => {
      let dataUrl = reader.result;
      const bytes = Math.round((dataUrl.length - dataUrl.indexOf(',') - 1) * 0.75);
      if (bytes > 5 * 1024 * 1024) {
        dataUrl = await compressToUnder5MB(dataUrl);
      }
      setRawImage({ url: dataUrl, source });
      setCrop({ unit: '%', width: 90, x: 5, y: 5 });
      setCompletedCrop(null);
      setIsCropping(true);
    };
    reader.readAsDataURL(file);
  };

  const handleImageUpload = (e) => {
    processFileInput(e.target.files[0], 'upload');
  };

  const handleCameraCapture = (e) => {
    processFileInput(e.target.files[0], 'camera');
  };

  const clearSelectedImage = () => {
    setUploadedImage(null);
    setUploadedImageSource('');
    setRawImage(null);
    setIsCropping(false);
    setCompletedCrop(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const applyCrop = useCallback(() => {
    const img = cropImgRef.current;
    if (!img || !completedCrop) {
      // No crop defined — use raw image as-is
      setUploadedImage(rawImage.url);
      setUploadedImageSource(rawImage.source);
      setIsCropping(false);
      return;
    }
    const scaleX = img.naturalWidth / img.width;
    const scaleY = img.naturalHeight / img.height;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(completedCrop.width * scaleX);
    canvas.height = Math.round(completedCrop.height * scaleY);
    const ctx = canvas.getContext('2d');
    ctx.drawImage(
      img,
      Math.round(completedCrop.x * scaleX),
      Math.round(completedCrop.y * scaleY),
      canvas.width,
      canvas.height,
      0, 0, canvas.width, canvas.height
    );
    setUploadedImage(canvas.toDataURL('image/jpeg', 0.92));
    setUploadedImageSource(rawImage.source);
    setIsCropping(false);
  }, [completedCrop, rawImage]);

  const skipCrop = () => {
    setUploadedImage(rawImage.url);
    setUploadedImageSource(rawImage.source);
    setIsCropping(false);
  };

  const processImage = async () => {
    if (!uploadedImage) return;

    track('generate_clicked', {
      language: selectedLanguage,
      input_type: 'image',
    });

    setIsLoading(true);
    setError('');
    setLoadingProgress(0);
    setProcessingComplete(false);
    setLoadingStage('Analysing image\u2026');
    if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);

    // Phase 1: 0 → 40% while vision model processes the image
    progressIntervalRef.current = setInterval(() => {
      setLoadingProgress(prev => prev >= 40 ? 40 : prev + Math.random() * 2);
    }, 400);

    try {
      const base64Response = await fetch(uploadedImage);
      const blob = await base64Response.blob();
      const file = new File([blob], 'image.jpg', { type: 'image/jpeg' });
      const formData = new FormData();
      formData.append('image', file);
      formData.append('language', selectedLanguage);

      const response = await fetch(`${API_URL}/api/process-image-stream`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        clearInterval(progressIntervalRef.current);
        const errData = await response.json().catch(() => ({}));
        if (response.status === 429) {
          setError(errData.message || 'Rate limit exceeded. Please try again later.');
        } else {
          setError(`Server error ${response.status}. Please try again.`);
        }
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let sseBuffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        sseBuffer += decoder.decode(value, { stream: true });
        const lines = sseBuffer.split('\n');
        sseBuffer = lines.pop();

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const raw = line.slice(6).trim();
          if (!raw) continue;
          try {
            const event = JSON.parse(raw);
            if (event.type === 'ttft') {
              clearInterval(progressIntervalRef.current);
              setLoadingStage('Generating flashcards\u2026');
              setLoadingProgress(42);
              // Phase 2: 42 → 90%
              progressIntervalRef.current = setInterval(() => {
                setLoadingProgress(prev => prev >= 90 ? 90 : prev + Math.random() * 2);
              }, 400);
            } else if (event.type === 'complete') {
              clearInterval(progressIntervalRef.current);
              setFlashcards(event.flashcards);
              track('generation_succeeded', {
                language: selectedLanguage,
                card_count: (event.flashcards || []).length,
              });
              saveGeneratedToVocab(event.flashcards);
              setLoadingProgress(100);
              setProcessingComplete(true);
              setLoadingStage('');
            } else if (event.type === 'error') {
              clearInterval(progressIntervalRef.current);
              setError(event.message || 'Failed to process image. Please try again.');
            }
          } catch (_) {}
        }
      }
    } catch (error) {
      console.error('Error processing image:', error);
      clearInterval(progressIntervalRef.current);
      track('generation_failed', { error: String((error && error.message) || error) });
      setError(`Failed: ${error.message || 'unknown error'}. Please try again.`);
    } finally {
      clearInterval(progressIntervalRef.current);
      setTimeout(() => {
        setIsLoading(false);
        setLoadingStage('');
      }, 800);
    }
  };

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, []);

  const clearAll = () => {
    setInputText('');
    setFlashcards([]);
    setUploadedImage(null);
    setUploadedImageSource('');
    setRawImage(null);
    setIsCropping(false);
    setCompletedCrop(null);
    setError('');
    setStudyMode('');
    setProcessingComplete(false);
  };

  const removeCard = (indexToRemove) => {
    setFlashcards(flashcards.filter((_, index) => index !== indexToRemove));
  };

  const [showDownloadModal, setShowDownloadModal] = useState(false);

  const downloadFlashcards = () => {
    if (flashcards.length === 0) return;
    setShowDownloadModal(true);
  };
  
  const downloadAsCSV = () => {
    track('download_clicked', { format: 'csv' });
    // Create CSV content
    const headers = "Word,Type,Translation\n";
    const csvContent = headers + 
      flashcards.map(card => 
        `"${card.word}","${card.type}","${card.translation}"`
      ).join("\n");
    
    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'extractstudy_cards.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setShowDownloadModal(false);
  };
  
  const downloadAsExcel = () => {
    track('download_clicked', { format: 'excel' });
    // Create workbook
    const workbook = XLSX.utils.book_new();
    const worksheetData = [
      ["Word", "Type", "Translation"]
    ];
    
    // Add flashcard data
    flashcards.forEach(card => {
      worksheetData.push([card.word, card.type, card.translation]);
    });
    
    // Create worksheet and add to workbook
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Flashcards");
    
    // Write to file and trigger download
    XLSX.writeFile(workbook, "extractstudy_cards.xlsx");
    setShowDownloadModal(false);
  };
  
  const downloadAsPDF = () => {
    if (flashcards.length === 0) return;
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 10;
    const cardWidth = (pageWidth - margin * 3) / 2;
    const cardHeight = (pageHeight - margin * 3) / 4;
    
    // Set font family for the whole document - using a more elegant serif font
    doc.setFont("times", "normal");
    
    // Add title
    doc.setFontSize(20);
    doc.setFont("times", "bold");
    doc.text("ExtractStudy Flashcards", pageWidth / 2, margin, { align: 'center' });
    doc.setFontSize(10);
    doc.setFont("times", "italic");
    doc.text(`Generated on ${new Date().toLocaleDateString()}`, pageWidth / 2, margin + 7, { align: 'center' });
    
    doc.setLineWidth(0.3);
    doc.setDrawColor(100, 100, 100);
    doc.setFontSize(12);
    
    // Generate flashcards with cut lines
    let currentPage = 1;
    let xPos = margin;
    let yPos = margin + 15;
    
    flashcards.forEach((card, index) => {
      // Check if we need a new page
      if (yPos > pageHeight - cardHeight - margin) {
        doc.addPage();
        currentPage++;
        yPos = margin;
      }
      
      // Draw card front with rounded corners and background
      doc.setDrawColor(100, 100, 100);
      doc.roundedRect(xPos, yPos, cardWidth, cardHeight, 3, 3);
      
      // Add subtle background color to the card front
      doc.setFillColor(250, 250, 252); // very light bluish background
      doc.roundedRect(xPos, yPos, cardWidth, cardHeight, 3, 3, 'F');
      
      // Card type label
      doc.setFontSize(9);
      doc.setFont("times", "italic");
      doc.setTextColor(80, 80, 80);
      // Capitalize first letter of the type
      const cardType = card.type.charAt(0).toUpperCase() + card.type.slice(1);
      doc.text(cardType, xPos + 5, yPos + 7);
      
      // Card word (large)
      doc.setFontSize(18);
      doc.setFont("times", "bold");
      doc.setTextColor(0, 0, 0);
      // Capitalize first letter of the word
      const formattedWord = card.word.charAt(0).toUpperCase() + card.word.slice(1);
      doc.text(formattedWord, xPos + cardWidth / 2, yPos + cardHeight / 2, { align: 'center' });
      
      // Draw card back with rounded corners and background
      doc.setDrawColor(100, 100, 100);
      doc.roundedRect(xPos + cardWidth, yPos, cardWidth, cardHeight, 3, 3);
      
      // Add subtle background color to the card back
      doc.setFillColor(252, 252, 250); // very light yellowish background
      doc.roundedRect(xPos + cardWidth, yPos, cardWidth, cardHeight, 3, 3, 'F');
      
      // Card translation
      doc.setFontSize(16);
      doc.setFont("times", "normal");
      doc.setTextColor(0, 0, 0);
      // Capitalize first letter of the translation
      const formattedTranslation = card.translation.charAt(0).toUpperCase() + card.translation.slice(1);
      doc.text(formattedTranslation, xPos + cardWidth + cardWidth / 2, yPos + cardHeight / 2, { align: 'center' });
      
      // Draw scissors icon and dotted line between cards
      doc.setDrawColor(150, 150, 150);
      doc.setLineDashPattern([1, 1], 0);
      
      // Horizontal dotted line (if not at top of page)
      if (yPos > margin + 15) {
        doc.setDrawColor(100, 100, 150);
        doc.setLineDashPattern([2, 2], 0);
        doc.line(margin, yPos, pageWidth - margin, yPos);
        // Add scissors icon
        doc.setFontSize(9);
        doc.setFont("times", "italic");
        doc.text("✂", margin - 5, yPos);
      }
      
      // Vertical dotted line between front and back
      doc.setDrawColor(100, 100, 150);
      doc.setLineDashPattern([2, 2], 0);
      doc.line(xPos + cardWidth, margin, xPos + cardWidth, pageHeight - margin);
      doc.setFontSize(9);
      doc.setFont("times", "italic");
      doc.text("✂", xPos + cardWidth, margin - 3);
      
      // Reset dash pattern
      doc.setLineDashPattern([], 0);
      doc.setDrawColor(100, 100, 100);
      
      // Update position for next card
      yPos += cardHeight;
      
      // If we've done two columns, reset to next row
      if ((index + 1) % 2 === 0) {
        xPos = margin;
        yPos += 10; // Space between rows
      } else {
        xPos += cardWidth;
      }
    });
    
    // Save PDF
    doc.save("extractstudy_flashcards.pdf");
    setShowDownloadModal(false);
  };
  
  // Function for folding-layout PDF download
  const downloadPdfWithFoldingLayout = () => {
    if (flashcards.length === 0) return;
    track('download_clicked', { format: 'pdf_folding' });
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 10;
    const cardWidth = (pageWidth - margin * 3) / 2;
    const cardHeight = (pageHeight - margin * 4) / 4;
    
    // Add title
    doc.setFontSize(16);
    doc.text("ExtractStudy Flashcards - Folding Layout", pageWidth / 2, margin, { align: 'center' });
    doc.setFontSize(10);
    doc.text(`Generated on ${new Date().toLocaleDateString()}`, pageWidth / 2, margin + 7, { align: 'center' });
    
    doc.setLineWidth(0.2);
    doc.setDrawColor(180, 180, 180);
    doc.setFontSize(12);
    
    // Generate flashcards for folding (front and back side by side)
    let currentPage = 1;
    let yPos = margin + 15;
    
    // Process cards in pairs for folding layout
    for (let i = 0; i < flashcards.length; i += 2) {
      // Check if we need a new page
      if (yPos > pageHeight - cardHeight - margin) {
        doc.addPage();
        currentPage++;
        yPos = margin;
      }
      
      // First card in the pair
      const card1 = flashcards[i];
      
      // Card front (left side)
      doc.rect(margin, yPos, cardWidth, cardHeight);
      
      // Card type label
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.text(card1.type, margin + 5, yPos + 7);
      
      // Card word (large)
      doc.setFontSize(14);
      doc.setTextColor(0, 0, 0);
      doc.text(card1.word, margin + cardWidth / 2, yPos + cardHeight / 2, { align: 'center' });
      
      // Card translation (right side - will be on back when folded)
      doc.rect(margin + cardWidth + margin, yPos, cardWidth, cardHeight);
      doc.setFontSize(14);
      doc.text(card1.translation, margin + cardWidth + margin + cardWidth / 2, yPos + cardHeight / 2, { align: 'center' });
      
      // Add fold line between cards
      doc.setDrawColor(150, 150, 150);
      doc.setLineDashPattern([1, 1], 0);
      doc.line(margin + cardWidth + margin/2, yPos - 5, margin + cardWidth + margin/2, yPos + cardHeight + 5);
      doc.text("Fold", margin + cardWidth + margin/2 - 5, yPos - 8);
      
      // Reset dash pattern
      doc.setLineDashPattern([], 0);
      doc.setDrawColor(0, 0, 0);
      
      // If we have a second card in this pair
      if (i + 1 < flashcards.length) {
        const card2 = flashcards[i + 1];
        
        // Add second row with some spacing
        yPos += cardHeight + 8;
        
        // Check if we need a new page for the second card
        if (yPos > pageHeight - cardHeight - margin) {
          doc.addPage();
          currentPage++;
          yPos = margin;
        }
        
        // Card front (left side)
        doc.rect(margin, yPos, cardWidth, cardHeight);
        
        // Card type label
        doc.setFontSize(8);
        doc.setTextColor(100, 100, 100);
        doc.text(card2.type, margin + 5, yPos + 7);
        
        // Card word (large)
        doc.setFontSize(14);
        doc.setTextColor(0, 0, 0);
        doc.text(card2.word, margin + cardWidth / 2, yPos + cardHeight / 2, { align: 'center' });
        
        // Card translation (right side - will be on back when folded)
        doc.rect(margin + cardWidth + margin, yPos, cardWidth, cardHeight);
        doc.setFontSize(14);
        doc.text(card2.translation, margin + cardWidth + margin + cardWidth / 2, yPos + cardHeight / 2, { align: 'center' });
        
        // Add fold line between cards
        doc.setDrawColor(150, 150, 150);
        doc.setLineDashPattern([1, 1], 0);
        doc.line(margin + cardWidth + margin/2, yPos - 5, margin + cardWidth + margin/2, yPos + cardHeight + 5);
        
        // Reset dash pattern
        doc.setLineDashPattern([], 0);
        doc.setDrawColor(0, 0, 0);
      }
      
      // Move to next pair of cards with spacing
      yPos += cardHeight + 15;
    }
    
    // Save PDF
    doc.save("extractstudy_folding_flashcards.pdf");
    setShowDownloadModal(false);
  };
  
  // Function for back-to-back layout PDF download
  const downloadPdfWithBackToBackLayout = () => {
    if (flashcards.length === 0) return;
    track('download_clicked', { format: 'pdf_back_to_back' });
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 10;
    const cardsPerRow = 3;
    const cardsPerCol = 4;
    const cardWidth = (pageWidth - (margin * (cardsPerRow + 1))) / cardsPerRow;
    const cardHeight = (pageHeight - (margin * (cardsPerCol + 1))) / cardsPerCol;
    
    // Create front side page
    doc.setFontSize(16);
    doc.text("ExtractStudy Flashcards - Front Side", pageWidth / 2, margin, { align: 'center' });
    doc.setFontSize(10);
    doc.text(`Print front and back separately, then match up`, pageWidth / 2, margin + 7, { align: 'center' });
    
    let cardIndex = 0;
    let xPos, yPos;
    
    // Draw fronts of cards (words in target language)
    for (let row = 0; row < cardsPerCol; row++) {
      for (let col = 0; col < cardsPerRow; col++) {
        if (cardIndex >= flashcards.length) break;
        
        xPos = margin + (col * (cardWidth + margin));
        yPos = margin * 2 + 10 + (row * (cardHeight + margin));
        
        // Draw card rectangle
        doc.setLineWidth(0.2);
        doc.setDrawColor(180, 180, 180);
        doc.rect(xPos, yPos, cardWidth, cardHeight);
        
        // Card type label
        doc.setFontSize(8);
        doc.setTextColor(100, 100, 100);
        doc.text(flashcards[cardIndex].type, xPos + 5, yPos + 7);
        
        // Card index for matching
        doc.setFontSize(6);
        doc.text(`#${cardIndex + 1}`, xPos + cardWidth - 5, yPos + 5, { align: 'right' });
        
        // Card word
        doc.setFontSize(14);
        doc.setTextColor(0, 0, 0);
        doc.text(flashcards[cardIndex].word, xPos + cardWidth / 2, yPos + cardHeight / 2, { align: 'center' });
        
        cardIndex++;
      }
    }
    
    // Add a new page for backs (translations)
    doc.addPage();
    
    doc.setFontSize(16);
    doc.text("ExtractStudy Flashcards - Back Side", pageWidth / 2, margin, { align: 'center' });
    doc.setFontSize(10);
    doc.text(`Print double-sided or print separately and match numbers`, pageWidth / 2, margin + 7, { align: 'center' });
    
    cardIndex = 0;
    
    // Draw backs of cards (translations)
    // Note: For double-sided printing, the card order needs to be reversed and flipped
    // This simple example just shows the backs in the same order as fronts
    for (let row = 0; row < cardsPerCol; row++) {
      for (let col = 0; col < cardsPerRow; col++) {
        if (cardIndex >= flashcards.length) break;
        
        xPos = margin + (col * (cardWidth + margin));
        yPos = margin * 2 + 10 + (row * (cardHeight + margin));
        
        // Draw card rectangle
        doc.setLineWidth(0.2);
        doc.setDrawColor(180, 180, 180);
        doc.rect(xPos, yPos, cardWidth, cardHeight);
        
        // Card index for matching
        doc.setFontSize(6);
        doc.text(`#${cardIndex + 1}`, xPos + cardWidth - 5, yPos + 5, { align: 'right' });
        
        // Card translation
        doc.setFontSize(14);
        doc.setTextColor(0, 0, 0);
        doc.text(flashcards[cardIndex].translation, xPos + cardWidth / 2, yPos + cardHeight / 2, { align: 'center' });
        
        cardIndex++;
      }
    }
    
    // Save PDF
    doc.save("extractstudy_backtoback_flashcards.pdf");
    setShowDownloadModal(false);
  };

  const openAddCardModal = () => {
    setShowAddCardModal(true);
  };

  const closeAddCardModal = () => {
    setShowAddCardModal(false);
  };

  const addCustomCard = () => {
    if (!newCard.word.trim() || !newCard.translation.trim()) return;
    
    setFlashcards([...flashcards, { 
      word: newCard.word,
      translation: newCard.translation,
      type: newCard.type 
    }]);
    
    // Reset form
    setNewCard({
      word: '',
      translation: '',
      type: 'noun'
    });
    
    closeAddCardModal();
  };

  const handleNewCardChange = (e) => {
    const { name, value } = e.target;
    setNewCard({ ...newCard, [name]: value });
  };

  const startRememberMode = () => {
    track('study_started', { mode: 'remember' });
    setStudyMode('remember');
    setActiveTab('study');
    setCurrentCardIndex(0);
    setShowTranslation(false);
  };

  const startTestMode = () => {
    track('study_started', { mode: 'test' });
    setStudyMode('test');
    setActiveTab('study');
    setCurrentCardIndex(0);
    setShowTranslation(false);
  };

  const flipCard = () => {
    if (isFlipping) return;
    
    setIsFlipping(true);
    setTimeout(() => {
      setShowTranslation(!showTranslation);
      setIsFlipping(false);
    }, 300);
  };

  const nextCard = () => {
    if (currentCardIndex < filteredCards.length - 1) {
      setCurrentCardIndex(currentCardIndex + 1);
      setShowTranslation(false);
    }
  };

  const prevCard = () => {
    if (currentCardIndex > 0) {
      setCurrentCardIndex(currentCardIndex - 1);
      setShowTranslation(false);
    }
  };

  // Get current card for study modes
  const currentCard = filteredCards[currentCardIndex];

  return (
    <div className="min-h-screen px-3 py-4 sm:p-4 transition-colors duration-300 app-shell">
      <div className="pointer-events-none fixed inset-0 opacity-65 mix-blend-screen" style={{background: 'radial-gradient(60% 50% at 20% 30%, rgba(139,208,255,0.10), transparent), radial-gradient(45% 40% at 80% 20%, rgba(74,163,255,0.08), transparent)'}}></div>
      {/* Top bar — desktop */}
      <div className="absolute top-4 right-4 z-30 hidden sm:flex items-center gap-3">
        {!session && (
          <button
            className="btn-secondary px-4 py-2 rounded-lg transition duration-200 font-medium text-sm border border-[var(--border)]"
            onClick={() => navigate('/login')}
          >
            Log in / Sign up
          </button>
        )}
        {session && (
          <button
            className="btn-secondary px-4 py-2 rounded-lg transition duration-200 font-medium text-sm border border-[var(--border)]"
            onClick={() => navigate('/vocabulary')}
          >
            Vocabulary Book
          </button>
        )}
        <button
          className="btn-secondary px-4 py-2 rounded-lg transition duration-200 font-medium text-sm border border-[var(--border)]"
          onClick={() => setShowAboutModal(true)}
        >
          About
        </button>
        {session && (
          <div className="relative" ref={profileMenuRef}>
            <button
              className="w-9 h-9 rounded-full flex items-center justify-center font-semibold text-sm text-white transition duration-200 border border-[var(--border)]"
              style={{ backgroundColor: '#3B82F6' }}
              onClick={() => setShowProfileMenu(v => !v)}
              aria-label="Account menu"
            >
              {(session.user?.email?.[0] || '?').toUpperCase()}
            </button>
            {showProfileMenu && (
              <div
                className="absolute right-0 mt-2 w-60 glass-panel rounded-xl border border-[var(--border)] p-3 shadow-xl"
                style={{ boxShadow: '0 0 30px rgba(0, 0, 0, 0.35)', animation: 'slideUp 0.2s ease-out' }}
              >
                <div className="text-[var(--muted)] text-xs mb-1">Signed in as</div>
                <div className="text-[var(--text)] text-sm font-medium break-all mb-3">
                  {session.user?.email}
                </div>
                <button
                  className="btn-secondary w-full px-3 py-2 rounded-lg text-sm font-medium border border-[var(--border)]"
                  onClick={signOut}
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Top bar — mobile hamburger */}
      <button
        className="sm:hidden absolute top-4 right-4 z-30 w-10 h-10 flex items-center justify-center rounded-lg btn-secondary border border-[var(--border)]"
        onClick={() => setMobileNavOpen(true)}
        aria-label="Open menu"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[var(--text)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      {/* Mobile side drawer */}
      {mobileNavOpen && (
        <div className="sm:hidden fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setMobileNavOpen(false)}
          ></div>
          <div
            className="absolute right-0 top-0 h-full w-72 max-w-[80vw] glass-panel border-l border-[var(--border)] p-5 flex flex-col"
            style={{ boxShadow: '0 0 40px rgba(0,0,0,0.45)', animation: 'slideInRight 0.25s ease-out' }}
          >
            <div className="flex justify-between items-center mb-6">
              <span className="text-[var(--accent)] font-semibold section-title">Menu</span>
              <button
                className="text-[var(--muted)] hover:text-[var(--text)] transition-colors"
                onClick={() => setMobileNavOpen(false)}
                aria-label="Close menu"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {session && (
              <div className="flex items-center gap-3 mb-5">
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center font-semibold text-sm text-white border border-[var(--border)]"
                  style={{ backgroundColor: '#3B82F6' }}
                >
                  {(session.user?.email?.[0] || '?').toUpperCase()}
                </div>
                <div className="text-[var(--text)] text-xs break-all min-w-0">
                  {session.user?.email}
                </div>
              </div>
            )}

            <nav className="flex flex-col gap-2">
              {!session && (
                <button
                  className="btn-secondary w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium border border-[var(--border)]"
                  onClick={() => { setMobileNavOpen(false); navigate('/login'); }}
                >
                  Log in / Sign up
                </button>
              )}
              {session && (
                <button
                  className="btn-secondary w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium border border-[var(--border)]"
                  onClick={() => { setMobileNavOpen(false); navigate('/vocabulary'); }}
                >
                  Vocabulary Book
                </button>
              )}
              <button
                className="btn-secondary w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium border border-[var(--border)]"
                onClick={() => { setMobileNavOpen(false); setShowAboutModal(true); }}
              >
                About
              </button>
            </nav>

            {session && (
              <button
                className="btn-secondary w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium border border-[var(--border)] mt-auto"
                onClick={() => { setMobileNavOpen(false); signOut(); }}
              >
                Sign out
              </button>
            )}
          </div>
        </div>
      )}
      
      {/* Hero */}
      <div className={`relative mx-auto max-w-4xl mb-10 pt-4 ${useCompactMobileHeader ? 'mt-2' : 'mt-6'}`}>
        <div className="hidden sm:block fixed z-20 top-4 left-4">
          <div className="logo-container">
            <div className="logo-frame"></div>
            <div className="logo-text">
              <span className="e">E</span><span className="s">S</span>
            </div>
          </div>
        </div>
        <div className="text-center px-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full accent-chip text-xs font-semibold tracking-wide mb-3">
            <span>AI Flashcards</span>
            <span className="h-1.5 w-1.5 rounded-full bg-current animate-pulse"></span>
          </div>
          <h1 className="section-title brand-title text-4xl sm:text-5xl font-bold mb-3">
            ExtractStudy
          </h1>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Paste text or upload an image, pick a language, and get clean, categorized vocabulary cards instantly.
          </p>
        </div>
      </div>

      <main className={`max-w-4xl mx-auto glass-panel rounded-2xl shadow-xl overflow-hidden border border-blue-900/20 backdrop-blur-md ${useCompactMobileHeader ? 'mt-20' : ''}`}
            style={{boxShadow: '0 0 40px rgba(0, 0, 0, 0.35)'}}>
        <div className="p-4 sm:p-6">
          {/* Tab Navigation */}
          <div className="flex mb-6 bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.06)] rounded-xl p-1 gap-1 sm:gap-2 overflow-x-auto">
            <button
              className={`flex-1 min-w-[52px] py-2.5 sm:py-3 px-2 sm:px-4 rounded-lg whitespace-nowrap text-xs sm:text-base leading-5 transition-colors duration-200 ${activeTab === 'text' ? 'btn-primary text-gray-900' : 'btn-secondary'}`}
              onClick={() => setActiveTab('text')}
            >
              Text
            </button>
            <button
              className={`flex-1 min-w-[120px] py-2.5 sm:py-3 px-2 sm:px-4 rounded-lg whitespace-nowrap text-xs sm:text-base leading-5 transition-colors duration-200 ${activeTab === 'ocr' ? 'btn-primary text-gray-900' : 'btn-secondary'}`}
              onClick={() => setActiveTab('ocr')}
            >
              Camera & Picture
            </button>
            {flashcards.length > 0 && (
              <button
                className={`flex-1 min-w-[52px] py-2.5 sm:py-3 px-2 sm:px-4 rounded-lg whitespace-nowrap text-xs sm:text-base leading-5 transition-colors duration-200 ${activeTab === 'study' ? 'btn-primary text-gray-900' : 'btn-secondary'}`}
                onClick={() => setActiveTab('study')}
              >
                Study
              </button>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-900/30 border border-red-800/50 text-red-300 rounded-lg">
              {error}
            </div>
          )}

          {/* Loading Animation */}
          {isLoading && (
            <div className="my-8 flex flex-col items-center">
              <div className="w-full max-w-md h-2 bg-gray-800 rounded-full mb-4 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500 theme-loading-bar"
                  style={{ width: `${loadingProgress}%` }}
                ></div>
              </div>
              {loadingStage && (
                <p className="text-sm font-medium text-[var(--text)] mb-1">{loadingStage}</p>
              )}
              <div className="flex items-center justify-center mt-3">
                <div className="leapfrog-container theme-loading-dots">
                  <div className="leapfrog-dot"></div>
                  <div className="leapfrog-dot"></div>
                  <div className="leapfrog-dot"></div>
                </div>
              </div>
            </div>
          )}

          {/* Text Input Tab */}
          {!isLoading && activeTab === 'text' && (
            <div>
              <div className="mb-3 flex justify-start">
                <div className="relative w-full sm:w-56" ref={languageDropdownRef}>
                  <button
                    type="button"
                    className="flex items-center justify-between w-full px-3 py-2 input-surface rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
                    onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
                  >
                    <span className="block truncate">
                      {supportedLanguages.find(lang => lang.code === selectedLanguage)?.name || 'Select Language'}
                    </span>
                    <svg className="w-5 h-5 ml-2 text-[var(--accent)]" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"></path>
                    </svg>
                  </button>
                  
                  {isLanguageDropdownOpen && (
                    <div 
                      className="absolute w-full mt-1 bg-[var(--panel-2)] border border-[var(--border)] rounded-md shadow-lg overflow-auto custom-scrollbar"
                      style={{ maxHeight: '260px', zIndex: 30 }}
                    >
                      <div className="sticky top-0 z-10 bg-[var(--panel-2)] p-2 border-b border-[var(--border)]">
                        <input
                          type="text"
                          className="w-full px-3 py-2 text-sm input-surface focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
                          placeholder="Search languages..."
                          value={searchLanguage}
                          onChange={(e) => setSearchLanguage(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      <ul className="py-1 overflow-auto text-sm custom-scrollbar">
                        {filteredLanguages.map((language) => (
                          <li
                            key={language.code}
                            className={`cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-[var(--panel)] ${selectedLanguage === language.code ? 'bg-[rgba(139,208,255,0.15)] text-[var(--text)]' : 'text-[var(--text)]'}`}
                            onClick={() => {
                              setSelectedLanguage(language.code);
                              setIsLanguageDropdownOpen(false);
                              setSearchLanguage('');
                            }}
                          >
                            {language.name}
                            {selectedLanguage === language.code && (
                              <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-[var(--accent)]">
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                                </svg>
                              </span>
                            )}
                          </li>
                        ))}
                        {filteredLanguages.length === 0 && (
                          <li className="cursor-default py-2 pl-3 pr-9 text-gray-400">
                            Language not supported
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              </div>

              <textarea
                className="w-full h-44 p-4 rounded-xl input-surface focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent)] focus:outline-none resize-none shadow-inner"
                placeholder="Paste or type text in any language here..."
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
              ></textarea>
              <div className="mt-5 flex flex-wrap gap-3 justify-end">
                {!processingComplete && flashcards.length === 0 && (
                  <button
                    className={`btn-primary text-gray-900 py-2.5 px-6 rounded-lg transition duration-200 flex items-center ${!inputText.trim() ? 'opacity-50 cursor-not-allowed' : ''}`}
                    onClick={processText}
                    disabled={!inputText.trim()}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                    Generate Flashcards
                  </button>
                )}
                
                {flashcards.length > 0 && (
                  <>
                    <button 
                      className="btn-secondary py-2 px-4 rounded-lg transition duration-200 flex items-center"
                      onClick={downloadFlashcards}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Download Options
                    </button>
                    
                    <button 
                      className="btn-secondary border border-[rgba(139,208,255,0.4)] py-2 px-4 rounded-lg transition duration-200 flex items-center"
                      onClick={() => setActiveTab('study')}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                      </svg>
                      Study
                    </button>
                    <button 
                      className="btn-secondary py-2 px-4 rounded-lg transition duration-200 flex items-center"
                      onClick={openAddCardModal}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Add Flashcard
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          {/* OCR Tab */}
          {!isLoading && activeTab === 'ocr' && (
            <div>
              <div className="mb-4 flex justify-start">
                <div className="relative w-full sm:w-56" ref={languageDropdownRef}>
                  <button
                    type="button"
                    className="flex items-center justify-between w-full px-3 py-2 input-surface rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
                    onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
                  >
                    <span className="block truncate">
                      {supportedLanguages.find(lang => lang.code === selectedLanguage)?.name || 'Select Language'}
                    </span>
                    <svg className="w-5 h-5 ml-2 text-[var(--accent)]" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"></path>
                    </svg>
                  </button>
                  
                  {isLanguageDropdownOpen && (
                    <div 
                      className="absolute w-full mt-1 bg-[var(--panel-2)] border border-[var(--border)] rounded-md shadow-lg overflow-auto custom-scrollbar"
                      style={{ maxHeight: '250px', zIndex: 30 }}
                    >
                      <div className="sticky top-0 z-10 bg-[var(--panel-2)] p-2 border-b border-[var(--border)]">
                        <input
                          type="text"
                          className="w-full px-3 py-2 text-sm input-surface focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
                          placeholder="Search languages..."
                          value={searchLanguage}
                          onChange={(e) => setSearchLanguage(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      <ul className="py-1 overflow-auto text-sm custom-scrollbar">
                        {filteredLanguages.map((language) => (
                          <li
                            key={language.code}
                            className={`cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-[var(--panel)] ${selectedLanguage === language.code ? 'bg-[rgba(139,208,255,0.15)] text-[var(--text)]' : 'text-[var(--text)]'}`}
                            onClick={() => {
                              setSelectedLanguage(language.code);
                              setIsLanguageDropdownOpen(false);
                              setSearchLanguage('');
                            }}
                          >
                            {language.name}
                            {selectedLanguage === language.code && (
                              <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-[var(--accent)]">
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                                </svg>
                              </span>
                            )}
                          </li>
                        ))}
                        {filteredLanguages.length === 0 && (
                          <li className="cursor-default py-2 pl-3 pr-9 text-gray-400">
                            No languages found
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              </div>

              {isCropping && rawImage ? (
                /* ── Crop UI ── */
                <div className="rounded-2xl border border-[var(--border)] bg-[rgba(255,255,255,0.02)] overflow-hidden">
                  <div className="px-4 pt-3 pb-2 flex items-center justify-between gap-2 border-b border-[var(--border)]">
                    <span className="text-sm font-semibold text-[var(--text)]">Crop image</span>
                    <span className="text-xs text-[var(--muted)]">Drag to select, or skip</span>
                  </div>
                  <div className="flex justify-center bg-black/20 p-2">
                    <ReactCrop
                      crop={crop}
                      onChange={(c) => setCrop(c)}
                      onComplete={(c) => setCompletedCrop(c)}
                      style={{ maxHeight: '60vh' }}
                    >
                      <img
                        ref={cropImgRef}
                        src={rawImage.url}
                        alt="Crop preview"
                        style={{ maxHeight: '60vh', maxWidth: '100%', display: 'block' }}
                      />
                    </ReactCrop>
                  </div>
                  <div className="px-4 py-3 flex gap-2 justify-end">
                    <button
                      className="btn-secondary py-2 px-4 rounded-lg text-sm transition duration-200"
                      onClick={skipCrop}
                    >
                      Skip
                    </button>
                    <button
                      className="btn-primary text-gray-900 py-2 px-4 rounded-lg text-sm transition duration-200"
                      onClick={applyCrop}
                    >
                      <i className="fa fa-crop mr-1" aria-hidden="true"></i>
                      Apply Crop
                    </button>
                  </div>
                </div>
              ) : uploadedImage ? (
                /* ── Final preview ── */
                <div className="rounded-2xl border border-[var(--border)] bg-[rgba(255,255,255,0.02)] overflow-hidden">
                  <img
                    src={uploadedImage}
                    alt={uploadedImageSource === 'camera' ? 'Camera capture' : 'Uploaded picture'}
                    className="w-full max-h-80 object-contain"
                    style={{ background: 'rgba(0,0,0,0.15)' }}
                  />
                  <div className="px-4 py-3 flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-[var(--text)]">
                        {uploadedImageSource === 'camera' ? 'Camera capture selected' : 'Picture selected'}
                      </div>
                      <div className="text-sm text-[var(--muted)]">Ready to extract text and generate flashcards.</div>
                    </div>
                    <button
                      className="text-sm text-red-400 hover:text-red-300 transition-colors duration-200"
                      onClick={clearSelectedImage}
                    >
                      Remove Image
                    </button>
                  </div>
                </div>
              ) : (
                /* ── Choice cards ── */
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="input-option-card">
                    <div className="flex items-center gap-3">
                      <div className="input-option-icon shrink-0">
                        <i className="fa fa-camera" aria-hidden="true"></i>
                      </div>
                      <div className="min-w-0">
                        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--accent)]">Camera</div>
                        <p className="mt-0.5 text-sm text-[var(--muted)] leading-snug">Open your camera and take a photo.</p>
                      </div>
                    </div>
                    <div className="mt-3">
                      <button
                        className="btn-primary text-gray-900 w-full py-2.5 px-4 rounded-lg transition duration-200 flex items-center justify-center text-sm"
                        onClick={() => {
                          try {
                            sessionStorage.setItem('_es_cam_tab', 'ocr');
                            sessionStorage.setItem('_es_cam_state', JSON.stringify({ flashcards, inputText, selectedLanguage }));
                          } catch {}
                          cameraInputRef.current?.click();
                        }}
                      >
                        <i className="fa fa-camera mr-2" aria-hidden="true"></i>
                        Open Camera
                      </button>
                      <input
                        ref={cameraInputRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={handleCameraCapture}
                      />
                    </div>
                  </div>

                  <div className="input-option-card">
                    <div className="flex items-center gap-3">
                      <div className="input-option-icon shrink-0">
                        <i className="fa fa-download" aria-hidden="true"></i>
                      </div>
                      <div className="min-w-0">
                        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--accent)]">Picture</div>
                        <p className="mt-0.5 text-sm text-[var(--muted)] leading-snug">Upload a saved image from your device.</p>
                      </div>
                    </div>
                    <div className="mt-3">
                      <button
                        className="btn-secondary w-full py-2.5 px-4 rounded-lg transition duration-200 flex items-center justify-center text-sm"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <i className="fa fa-download mr-2" aria-hidden="true"></i>
                        Select Picture
                      </button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleImageUpload}
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-4 w-full sm:w-auto grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:gap-3 sm:justify-end">
                  <button
                    className={`btn-primary text-gray-900 text-sm py-2 px-3 rounded-lg transition duration-200 flex items-center justify-center ${!uploadedImage ? 'opacity-50 cursor-not-allowed' : ''}`}
                    onClick={processImage}
                    disabled={!uploadedImage}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Extract Text & Generate
                  </button>
                  
                  {processingComplete && (
                    <>
                      <button 
                        className="btn-secondary text-sm py-2 px-3 rounded-lg transition duration-200 flex items-center justify-center"
                        onClick={downloadFlashcards}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        Download Options
                      </button>
                      
                      <button 
                        className="btn-secondary border border-[rgba(139,208,255,0.4)] text-sm py-2 px-3 rounded-lg transition duration-200 flex items-center justify-center"
                        onClick={() => setActiveTab('study')}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                        </svg>
                        Study
                      </button>
                      <button 
                        className="btn-secondary text-sm py-2 px-3 rounded-lg transition duration-200 flex items-center justify-center"
                        onClick={openAddCardModal}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add Flashcard
                      </button>
                    </>
                  )}
              </div>

            </div>
          )}

          {/* Study Mode Tab */}
          {!isLoading && activeTab === 'study' && (
            <div>
              {/* Word Type Filters */}
              <div className="bg-[var(--panel-2)] p-4 rounded-xl mb-6 border border-[var(--border)]">
                <h3 className="text-lg text-[var(--text)] mb-2 section-title">Filter Word Types</h3>
                <div className="flex flex-wrap gap-3">
                  <button
                    className={`px-3 py-1 rounded-full text-sm transition-colors duration-200 ${filters.noun ? 'accent-chip' : 'pill text-[var(--muted)]'}`}
                    onClick={() => setFilters({...filters, noun: !filters.noun})}
                  >
                    Nouns
                  </button>
                  <button
                    className={`px-3 py-1 rounded-full text-sm transition-colors duration-200 ${filters.verb ? 'accent-chip' : 'pill text-[var(--muted)]'}`}
                    onClick={() => setFilters({...filters, verb: !filters.verb})}
                  >
                    Verbs
                  </button>
                  <button
                    className={`px-3 py-1 rounded-full text-sm transition-colors duration-200 ${filters.adjective ? 'accent-chip' : 'pill text-[var(--muted)]'}`}
                    onClick={() => setFilters({...filters, adjective: !filters.adjective})}
                  >
                    Adjectives
                  </button>
                  <button
                    className={`px-3 py-1 rounded-full text-sm transition-colors duration-200 ${filters.other ? 'accent-chip' : 'pill text-[var(--muted)]'}`}
                    onClick={() => setFilters({...filters, other: !filters.other})}
                  >
                    Other
                  </button>
                </div>
              </div>
              
              {!studyMode ? (
                <div className="text-center py-8">
                  <h2 className="text-xl font-semibold text-[var(--text)] mb-6 section-title">Choose Study Mode</h2>
                  
                  <div className="flex justify-center space-x-4 mb-6">
                    <button 
                      className="btn-primary text-gray-900 py-3 px-8 rounded-lg transition duration-200 flex items-center"
                      onClick={startRememberMode}
                      disabled={filteredCards.length === 0}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Remember Cards
                    </button>
                    <button 
                      className="btn-secondary border border-[rgba(167,139,250,0.5)] py-3 px-8 rounded-lg transition duration-200 flex items-center"
                      onClick={startTestMode}
                      disabled={filteredCards.length === 0}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      Test Yourself
                    </button>
                  </div>
                  
                  <div className="mb-6">
                    <h3 className="text-[var(--text)] mb-2">Display Settings</h3>
                    <div className="flex items-center justify-center space-x-4">
                      <button 
                        className={`px-4 py-2 rounded-lg transition duration-200 ${startWithSourceLang ? 'accent-chip' : 'pill text-[var(--muted)]'}`}
                        onClick={() => setStartWithSourceLang(true)}
                      >
                        Source Language First
                      </button>
                      <button 
                        className={`px-4 py-2 rounded-lg transition duration-200 ${!startWithSourceLang ? 'accent-chip' : 'pill text-[var(--muted)]'}`}
                        onClick={() => setStartWithSourceLang(false)}
                      >
                        Translation First
                      </button>
                    </div>
                  </div>
                  
                  {filteredCards.length === 0 && (
                    <p className="text-red-400 mt-4">No cards match your current filters.</p>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <div className="mb-4 text-center">
                    <p className="text-sm text-gray-400 mb-1">
                      Card {currentCardIndex + 1} of {filteredCards.length}
                    </p>
                    <h3 className="text-lg font-medium text-blue-400">
                      {studyMode === 'remember' ? 'Remember Mode' : 'Test Mode'}
                    </h3>
                    {studyMode === 'test' && 
                      <p className="text-xs text-gray-500 mt-1">
                        {showTranslation ? "Click to go to next card" : "Click card to flip"}
                      </p>
                    }
                  </div>
                  
                  {/* Flashcard with flip animation */}
                  <div
                    className={`rounded-xl w-full max-w-md h-64 cursor-pointer perspective-1000 my-6 shadow-xl transition-transform duration-300 ${isFlipping ? 'scale-95' : ''} relative`}
                    style={{
                      background: 'linear-gradient(145deg, rgba(12,18,32,0.95), rgba(8,12,22,0.95))',
                      border: '1px solid var(--border)',
                      boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
                      perspective: '1000px'
                    }}
                    onClick={() => studyMode === 'test' && flipCard()}
                  >
                    <button 
                      className="absolute top-2 right-2 z-10 text-gray-500 hover:text-red-400 bg-gray-800 rounded-full p-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeCard(flashcards.indexOf(currentCard));
                        if (currentCardIndex >= filteredCards.length - 1) {
                          setCurrentCardIndex(Math.max(0, currentCardIndex - 1));
                        }
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                    <div 
                      className={`relative w-full h-full text-center flex flex-col items-center justify-center p-6 rounded-xl transform-style-3d transition-transform duration-300`}
                      style={{
                        transformStyle: 'preserve-3d',
                        transform: showTranslation ? 'rotateY(180deg)' : 'rotateY(0deg)',
                        transition: 'transform 0.6s'
                      }}
                    >
                      {/* Front of Card */}
                      <div 
                        className="absolute w-full h-full backface-hidden flex flex-col items-center justify-center p-6"
                        style={{
                          backfaceVisibility: 'hidden'
                        }}
                      >
                        <span className="text-xs accent-chip px-2 py-1 rounded-full mb-4">
                          {currentCard?.type}
                        </span>
                        <h3 className="text-3xl font-bold mb-2 text-[var(--text)]">
                          {startWithSourceLang ? currentCard?.word : currentCard?.translation}
                        </h3>
                        
                        {studyMode === 'remember' && (
                          <p className="text-xl text-[var(--muted)] mt-4">
                            {startWithSourceLang ? currentCard?.translation : currentCard?.word}
                          </p>
                        )}
                      </div>
                      
                      {/* Back of Card */}
                      <div 
                        className="absolute w-full h-full backface-hidden flex flex-col items-center justify-center p-6 rotate-y-180"
                        style={{
                          backfaceVisibility: 'hidden',
                          transform: 'rotateY(180deg)'
                        }}
                      >
                        <span className="text-xs accent-chip px-2 py-1 rounded-full mb-4">
                          {currentCard?.type}
                        </span>
                        <h3 className="text-3xl font-bold mb-2 text-[var(--text)]">
                          {!startWithSourceLang ? currentCard?.word : currentCard?.translation}
                        </h3>
                      </div>
                    </div>
                  </div>
                  
                  {/* Navigation Buttons */}
                  <div className="flex justify-center space-x-4 mt-6">
                    <button 
                      className={`px-4 py-2 rounded-lg pill transition-all duration-200 ${currentCardIndex === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'}`}
                      onClick={prevCard}
                      disabled={currentCardIndex === 0}
                    >
                      Previous
                    </button>
                    
                    {studyMode === 'test' && !showTranslation && (
                      <button 
                        className="px-4 py-2 rounded-lg btn-primary text-gray-900 transition-all duration-200"
                        onClick={flipCard}
                      >
                        Reveal
                      </button>
                    )}
                    
                    <button 
                      className={`px-4 py-2 rounded-lg pill transition-all duration-200 ${currentCardIndex === filteredCards.length - 1 ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'}`}
                      onClick={nextCard}
                      disabled={currentCardIndex === filteredCards.length - 1}
                    >
                      Next
                    </button>
                  </div>
                  
                  <button 
                    className="mt-8 text-gray-400 hover:text-gray-300"
                    onClick={() => setStudyMode('')}
                  >
                    Back to Study Options
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Flashcards Output */}
          {!isLoading && flashcards.length > 0 && activeTab !== 'study' && (
            <div className="mt-8">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
                <h2 className="text-xl font-semibold text-[var(--text)] section-title">Generated Flashcards</h2>
                <div className="flex flex-wrap items-center gap-3 justify-end">
                  {/* Word Type Filters */}
                  <div className="flex flex-wrap gap-2">
                    <button
                      className={`px-2.5 py-1 rounded-full text-xs transition-colors duration-200 ${filters.noun ? 'accent-chip' : 'pill text-[var(--muted)]'}`}
                      onClick={() => setFilters({...filters, noun: !filters.noun})}
                    >
                      Nouns
                    </button>
                    <button
                      className={`px-2.5 py-1 rounded-full text-xs transition-colors duration-200 ${filters.verb ? 'accent-chip' : 'pill text-[var(--muted)]'}`}
                      onClick={() => setFilters({...filters, verb: !filters.verb})}
                    >
                      Verbs
                    </button>
                    <button
                      className={`px-2.5 py-1 rounded-full text-xs transition-colors duration-200 ${filters.adjective ? 'accent-chip' : 'pill text-[var(--muted)]'}`}
                      onClick={() => setFilters({...filters, adjective: !filters.adjective})}
                    >
                      Adj.
                    </button>
                    <button
                      className={`px-2.5 py-1 rounded-full text-xs transition-colors duration-200 ${filters.other ? 'accent-chip' : 'pill text-[var(--muted)]'}`}
                      onClick={() => setFilters({...filters, other: !filters.other})}
                    >
                      Other
                    </button>
                  </div>

                  <button 
                    className="text-[var(--muted)] hover:text-[var(--text)] text-sm"
                    onClick={clearAll}
                  >
                    Clear All
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredCards.length === 0 ? (
                  <div className="col-span-2 text-center py-6 text-gray-400">
                    No cards match your current filters.
                  </div>
                ) : (
                  filteredCards.map((card, index) => (
                    <div 
                      key={index} 
                      className="rounded-lg p-4 transition duration-200 relative group"
                      style={{
                        background: 'linear-gradient(150deg, rgba(12,18,32,0.95), rgba(10,14,24,0.92))',
                        border: '1px solid var(--border)',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.25)'
                      }}
                    >
                      <button 
                        className="absolute top-2 right-2 text-gray-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                        onClick={() => removeCard(flashcards.indexOf(card))}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="text-lg font-medium text-[var(--text)]">{card.word}</h3>
                        <span className="text-xs accent-chip px-2 py-1 rounded-full">
                          {card.type}
                        </span>
                      </div>
                      <p className="text-[var(--muted)]">{card.translation}</p>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Add Card Modal */}
      {showAddCardModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-gray-900 p-6 rounded-lg w-full max-w-md shadow-xl border border-blue-800/30" 
               style={{boxShadow: '0 0 30px rgba(59, 130, 246, 0.2)'}}>
            <h2 className="text-xl font-semibold text-blue-400 mb-4 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Custom Flashcard
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-gray-400 text-sm mb-1">Original Word</label>
                <input
                  type="text"
                  name="word"
                  value={newCard.word}
                  onChange={handleNewCardChange}
                  className="w-full p-2 rounded bg-gray-800 border border-gray-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                  placeholder="e.g. bonjour"
                />
              </div>
              
              <div>
                <label className="block text-gray-400 text-sm mb-1">Translation</label>
                <input
                  type="text"
                  name="translation"
                  value={newCard.translation}
                  onChange={handleNewCardChange}
                  className="w-full p-2 rounded bg-gray-800 border border-gray-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                  placeholder="e.g. hello"
                />
              </div>
              
              <div>
                <label className="block text-gray-400 text-sm mb-1">Word Type</label>
                <select
                  name="type"
                  value={newCard.type}
                  onChange={handleNewCardChange}
                  className="w-full p-2 rounded bg-gray-800 border border-gray-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                >
                  <option value="noun">Noun</option>
                  <option value="verb">Verb</option>
                  <option value="adjective">Adjective</option>
                  <option value="adverb">Adverb</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
            
            <div className="flex justify-end mt-6 space-x-3">
              <button
                className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600"
                onClick={closeAddCardModal}
              >
                Cancel
              </button>
              <button
                className={`bg-blue-600 hover:bg-blue-500 text-white py-2 px-4 rounded-lg transition duration-200 ${!newCard.word.trim() || !newCard.translation.trim() ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={addCustomCard}
                disabled={!newCard.word.trim() || !newCard.translation.trim()}
              >
                Add Card
              </button>
            </div>
          </div>
        </div>
      )}

      <footer className="mt-12 text-center text-gray-500 text-sm">
        <p>&copy; {new Date().getFullYear()} ExtractStudy &mdash; Your AI Vocabulary Assistant</p>
        <p className="mt-2 space-x-3">
          <a href="/privacy" className="text-[var(--muted)] hover:text-[var(--text)] transition-colors">
            Privacy Policy
          </a>
          <span className="text-[var(--border)]">&middot;</span>
          <a href="/terms" className="text-[var(--muted)] hover:text-[var(--text)] transition-colors">
            Terms of Service
          </a>
        </p>
      </footer>
      {/* Download Options Modal */}
      {showDownloadModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div 
            className="bg-gray-900 p-6 rounded-lg w-full max-w-md shadow-xl border border-blue-800/30 animate-slide-up" 
            style={{
              boxShadow: '0 0 20px rgba(59, 130, 246, 0.15)',
              animation: 'slideUp 0.3s ease-out'
            }}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-blue-400">Download Options</h2>
              <button 
                className="text-gray-400 hover:text-gray-300 transition-colors duration-200"
                onClick={() => setShowDownloadModal(false)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <p className="text-gray-300 mb-6">Choose your preferred download format:</p>
            
            <div className="grid grid-cols-1 gap-4">
              {/* PDF options with sub-options */}
              <div>
                <button
                  className="flex items-center justify-between bg-blue-600/20 hover:bg-blue-600/30 text-white p-4 rounded-lg transition duration-200 w-full mb-2"
                  onClick={() => {
                    // This button now just expands the sub-options
                    const subOptions = document.getElementById('pdf-sub-options');
                    if (subOptions) {
                      subOptions.style.maxHeight = subOptions.style.maxHeight ? null : subOptions.scrollHeight + 'px';
                    }
                  }}
                >
                  <div className="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-3 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                    </svg>
                    <div className="text-left">
                      <div className="font-medium">PDF Flashcards</div>
                      <div className="text-sm text-gray-400">Printable cards with cut lines</div>
                    </div>
                  </div>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {/* PDF Sub-options */}
                <div 
                  id="pdf-sub-options"
                  className="overflow-hidden transition-all duration-300 max-h-0 pl-4 border-l-2 border-blue-900/30 ml-3"
                >
                  <div className="grid grid-cols-1 gap-2 pt-1 pb-2">
                    <button
                      className="flex items-center bg-blue-800/20 hover:bg-blue-800/30 text-white p-3 rounded-lg transition duration-200"
                      onClick={downloadPdfWithFoldingLayout}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                      </svg>
                      <div className="text-left">
                        <div className="font-medium">Folding Layout</div>
                        <div className="text-xs text-gray-400">Cards side-by-side for folding</div>
                      </div>
                    </button>
                    
                    <button
                      className="flex items-center bg-blue-800/20 hover:bg-blue-800/30 text-white p-3 rounded-lg transition duration-200"
                      onClick={downloadPdfWithBackToBackLayout}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                      </svg>
                      <div className="text-left">
                        <div className="font-medium">Back-to-Back Layout</div>
                        <div className="text-xs text-gray-400">Print words on one side, meanings on reverse</div>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
              
              <button
                className="flex items-center justify-between bg-blue-600/20 hover:bg-blue-600/30 text-white p-4 rounded-lg transition duration-200"
                onClick={downloadAsExcel}
              >
                <div className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-3 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                  </svg>
                  <div className="text-left">
                    <div className="font-medium">Excel Spreadsheet</div>
                    <div className="text-sm text-gray-400">Editable table format (.xlsx)</div>
                  </div>
                </div>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
              
              <button
                className="flex items-center justify-between bg-blue-600/20 hover:bg-blue-600/30 text-white p-4 rounded-lg transition duration-200"
                onClick={downloadAsCSV}
              >
                <div className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-3 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                  </svg>
                  <div className="text-left">
                    <div className="font-medium">CSV File</div>
                    <div className="text-sm text-gray-400">Universal data format (.csv)</div>
                  </div>
                </div>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Privacy Policy Modal */}
      {showPrivacyModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div 
            className="bg-gray-900 p-6 rounded-lg w-full max-w-lg shadow-xl border border-blue-800/30 animate-slide-up" 
            style={{
              boxShadow: '0 0 20px rgba(59, 130, 246, 0.15)',
              animation: 'slideUp 0.3s ease-out'
            }}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-blue-400">Privacy Policy</h2>
              <button 
                className="text-gray-400 hover:text-gray-300 transition-colors duration-200"
                onClick={() => setShowPrivacyModal(false)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="text-gray-300 text-sm space-y-4 pr-2" style={{maxHeight: "60vh", overflowY: "auto"}}>
              <p>ExtractStudy respects your privacy. Our service processes text and images you provide to generate flashcards but does not permanently store your content.</p>
              <h3 className="text-blue-400 font-medium mt-4">How We Process Your Data</h3>
              <p>When you upload text or images, we temporarily process this content using advanced language processing technology to generate flashcards. Your uploaded content is not permanently stored.</p>
              <h3 className="text-blue-400 font-medium mt-4">Essential Cookies</h3>
              <p>We use essential cookies to ensure the proper functioning of our website.</p>
              <h3 className="text-blue-400 font-medium mt-4">Third-Party Services</h3>
              <p>ExtractStudy uses external language processing services to analyze and translate text according to their privacy policies.</p>
              <h3 className="text-blue-400 font-medium mt-4">Contact</h3>
              <p>For questions about this privacy policy, please contact us at: <a href="mailto:extractstudy@proton.me" className="text-blue-400">extractstudy@proton.me</a></p>
            </div>
            <div className="mt-6 flex justify-end">
              <button
                className="bg-blue-600 hover:bg-blue-500 text-white py-2 px-4 rounded-lg transition duration-200"
                onClick={() => setShowPrivacyModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* About Modal */}
      {showAboutModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div 
            className="glass-panel p-6 rounded-xl w-full max-w-lg shadow-xl border border-[var(--border)]" 
            style={{
              boxShadow: '0 0 30px rgba(0, 0, 0, 0.35)',
              animation: 'slideUp 0.3s ease-out'
            }}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-[var(--accent)] section-title">About</h2>
              <button 
                className="text-[var(--muted)] hover:text-[var(--text)] transition-colors duration-200"
                onClick={() => setShowAboutModal(false)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="text-[var(--text)] text-sm space-y-4 pr-2 custom-scrollbar" style={{maxHeight: "60vh", overflowY: "auto"}}>
              <p>ExtractStudy instantly converts foreign language text or images into English flashcards. Our AI automatically identifies words, determines their type (noun, verb, adjective), and provides English translations - helping you build vocabulary faster in 60 languages.</p>

              <h3 className="text-[var(--accent)] font-medium mt-4">FAQ</h3>

              <div className="space-y-4 mt-2">
                <div className="border border-[var(--border)] rounded-lg overflow-hidden bg-[var(--panel-2)]/60">
                  <button
                    className="w-full p-3 text-left bg-transparent hover:bg-[rgba(255,255,255,0.04)] flex justify-between items-center"
                    onClick={(e) => {
                      const content = e.currentTarget.nextElementSibling;
                      content.style.maxHeight = content.style.maxHeight ? null : content.scrollHeight + 'px';
                    }}
                  >
                    <span>How does ExtractStudy work?</span>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <div className="max-h-0 overflow-hidden transition-all duration-300 bg-[rgba(255,255,255,0.02)] text-[var(--muted)]">
                    <div className="p-3">
                      ExtractStudy uses advanced language processing technology to analyze text in various languages, identify key vocabulary words by type (nouns, verbs, adjectives), and automatically translate them to create interactive flashcards for effective language learning.
                    </div>
                  </div>
                </div>

                <div className="border border-[var(--border)] rounded-lg overflow-hidden bg-[var(--panel-2)]/60">
                  <button
                    className="w-full p-3 text-left bg-transparent hover:bg-[rgba(255,255,255,0.04)] flex justify-between items-center"
                    onClick={(e) => {
                      const content = e.currentTarget.nextElementSibling;
                      content.style.maxHeight = content.style.maxHeight ? null : content.scrollHeight + 'px';
                    }}
                  >
                    <span>What languages are supported?</span>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <div className="max-h-0 overflow-hidden transition-all duration-300 bg-[rgba(255,255,255,0.02)] text-[var(--muted)]">
                    <div className="p-3">
                      ExtractStudy supports 60 languages including Spanish, French, German, Italian, Japanese, Chinese, Russian, Arabic, Portuguese, Dutch, Korean, Hindi, Bengali, Thai, Vietnamese, Swahili, and many more. Our language learning tool works with most major world languages, and you can select your target language from the dropdown menu when processing text or images.
                    </div>
                  </div>
                </div>

                <div className="border border-[var(--border)] rounded-lg overflow-hidden bg-[var(--panel-2)]/60">
                  <button
                    className="w-full p-3 text-left bg-transparent hover:bg-[rgba(255,255,255,0.04)] flex justify-between items-center"
                    onClick={(e) => {
                      const content = e.currentTarget.nextElementSibling;
                      content.style.maxHeight = content.style.maxHeight ? null : content.scrollHeight + 'px';
                    }}
                  >
                    <span>Are there usage limits?</span>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <div className="max-h-0 overflow-hidden transition-all duration-300 bg-[rgba(255,255,255,0.02)] text-[var(--muted)]">
                    <div className="p-3">
                      Yes, our free service has some usage limits to ensure fair access for all language learners. Currently, you can process up to 5 requests per minute and 50 requests per day. For extensive vocabulary learning needs or classroom use, please consider supporting us with a donation.
                    </div>
                  </div>
                </div>

                <div className="border border-[var(--border)] rounded-lg overflow-hidden bg-[var(--panel-2)]/60">
                  <button
                    className="w-full p-3 text-left bg-transparent hover:bg-[rgba(255,255,255,0.04)] flex justify-between items-center"
                    onClick={(e) => {
                      const content = e.currentTarget.nextElementSibling;
                      content.style.maxHeight = content.style.maxHeight ? null : content.scrollHeight + 'px';
                    }}
                  >
                    <span>Is my data secure?</span>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <div className="max-h-0 overflow-hidden transition-all duration-300 bg-[rgba(255,255,255,0.02)] text-[var(--muted)]">
                    <div className="p-3">
                      Yes. ExtractStudy prioritizes your privacy while learning languages. We process your text and images to generate flashcards but do not permanently store your content. Your language data is only temporarily processed to create the flashcards you see, and the raw text/images are not saved on our servers after processing.
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-6 flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap gap-2">
                <button
                  className="btn-secondary py-2 px-3 rounded-lg text-sm font-medium border border-[var(--border)]"
                  onClick={() => { setShowAboutModal(false); navigate('/privacy'); }}
                >
                  Privacy Policy
                </button>
                <button
                  className="btn-secondary py-2 px-3 rounded-lg text-sm font-medium border border-[var(--border)]"
                  onClick={() => { setShowAboutModal(false); navigate('/terms'); }}
                >
                  Terms of Service
                </button>
              </div>
              <button
                className="btn-primary text-gray-900 py-2 px-4 rounded-lg transition duration-200"
                onClick={() => setShowAboutModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExtractStudyApp;
